import { DatePicker, Select } from 'antd';
import ChartComponent, { CHART_TYPES } from '../../../components/chartComponent/ChartComponent';
import { useEffect, useState, useRef, useMemo } from 'react';
import FormItem from 'antd/es/form/FormItem';
import ApexCharts from 'apexcharts';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import type { Basket } from '../../../types/basket';
import type { Location } from '../../../types/referenceData';
import { analysisService } from '../../../api/services/analysisService';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import {
  useGetBCHOverlay,
  useGetIhmaPriceBreakdown,
} from '../../../api/hooks/useExternalDataQuery';
import { PriceSourceTypeEnum } from '../../../enums/PriceSourceTypeEnum';
import dayjs from 'dayjs';
import { disableFutureDates } from '../../../utils/disableDates';
import { normalize } from '../../../utils/normalizeWords';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import { downloadCustomCSV } from '../../../helpers/utilityHelper';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import {
  ExternalDataUploadTypeEnum,
  getExternalDataUploadTypeOptions,
} from '../../../enums/ExternalDataUploadTypeEnum';
import { TFunction } from 'i18next';
import { getBCHDivisionAndRegion } from '../getBCHFilterData';
import { CpiModeEnum, getCPIModeOptions } from '../../../enums/CpiModeEnum';
interface PriceVarianceChartProps {
  t: TFunction<['analysisAndReports', 'dataUpload']>;
}

type PriceSourceType = PriceSourceTypeEnum | null | undefined;

const PriceVarianceChart = ({ t }: PriceVarianceChartProps) => {
  const today = dayjs();
  const oneMonthAgo = today.subtract(1, 'month');

  const { errorNotification } = useNotificationContext();

  const [selectedDateRange, setSelectedDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>([
    oneMonthAgo,
    today,
  ]);

  // Price source selection
  const [priceSource, setPriceSource] = useState<PriceSourceType>(null);

  // IHMA-specific states

  //BCH specific states
  const [selectedBCHType, setSelectedBCHType] = useState<ExternalDataUploadTypeEnum | null>(null);
  const [selectedBCHDivision, setSelectedBCHDivision] = useState<string | undefined>(undefined);
  const [selectedBCHRegion, setSelectedBCHRegion] = useState<string | undefined>(undefined);
  const [selectedBCHMode, setSelectedBCHMode] = useState<string | undefined>(CpiModeEnum.MONTHLY);
  const [BCHDivisionsOptions, setBCHDivisionsOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [BCHRegionsOptions, setBCHRegionsOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const isDivisionFilterVisible = (type: ExternalDataUploadTypeEnum | null) => {
    return (
      type === ExternalDataUploadTypeEnum.CPI_DIVISION_MONTHLY_SERIES ||
      type === ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_DIVISION
    );
  };

  const isRegionFilterVisible = (type: ExternalDataUploadTypeEnum | null) => {
    return (
      type === ExternalDataUploadTypeEnum.CPI_REGION_MONTHLY_SERIES ||
      type === ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_REGION
    );
  };

  // Division and Region filter values for BCH
  useEffect(() => {
    if (
      priceSource !== PriceSourceTypeEnum.BCH ||
      (BCHDivisionsOptions?.length > 0 && BCHRegionsOptions?.length > 0)
    )
      return;

    const loadBCHDivisionAndRegionData = async () => {
      try {
        const response = await getBCHDivisionAndRegion();
        const divisionData = response?.divisions || [];
        const regionsData = response?.regions || [];

        const uniqueDivisions: any = Array.from(
          new Set(
            divisionData?.map((name: string) => ({
              label: String(name),
              value: String(name),
            }))
          )
        );

        const uniqueRegions: any = Array.from(
          new Set(
            regionsData?.map((name: string) => ({
              label: String(name),
              value: String(name),
            }))
          )
        );

        const sortedDivisions = uniqueDivisions?.sort((a: any, b: any) =>
          a?.label?.localeCompare(b?.label)
        );
        const sortedRegions = uniqueRegions?.sort((a: any, b: any) =>
          a?.label?.localeCompare(b?.label)
        );

        setBCHDivisionsOptions(sortedDivisions);
        setBCHRegionsOptions(sortedRegions);

        if (isDivisionFilterVisible(selectedBCHType) && sortedDivisions?.length > 0) {
          setSelectedBCHDivision(sortedDivisions[0].value);
        }
        if (isRegionFilterVisible(selectedBCHType) && sortedRegions?.length > 0) {
          setSelectedBCHRegion(sortedRegions[0].value);
        }
      } catch (error) {
        errorNotification(t('failedToLoadFilters'));
      }
    };

    loadBCHDivisionAndRegionData();
  }, [priceSource, errorNotification]);

  // Set default value for Division filter when visible
  useEffect(() => {
    const isDivisionVisible =
      selectedBCHType &&
      (selectedBCHType === ExternalDataUploadTypeEnum.CPI_DIVISION_MONTHLY_SERIES ||
        selectedBCHType === ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_DIVISION);

    if (isDivisionVisible && BCHDivisionsOptions?.length > 0 && !selectedBCHDivision) {
      setSelectedBCHDivision(BCHDivisionsOptions[0].value);
    }
  }, [selectedBCHType, BCHDivisionsOptions, selectedBCHDivision]);

  // Set default value for Region filter when visible
  useEffect(() => {
    const isRegionVisible =
      selectedBCHType &&
      (selectedBCHType === ExternalDataUploadTypeEnum.CPI_REGION_MONTHLY_SERIES ||
        selectedBCHType === ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_REGION);

    if (isRegionVisible && BCHRegionsOptions?.length > 0 && !selectedBCHRegion) {
      setSelectedBCHRegion(BCHRegionsOptions[0].value);
    }
  }, [selectedBCHType, BCHRegionsOptions, selectedBCHRegion]);

  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);

  const [filterLoading, setFilterLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [baskets, setBaskets] = useState<Basket[]>([]);
  const [presentations, setPresentations] = useState<any>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [municipalities, setMunicipalities] = useState<Location[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [selectedBasket, setSelectedBasket] = useState<number | undefined>(undefined);
  const [selectedPresentations, setSelectedPresentations] = useState<number[]>([]);
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<string[]>([]);
  const [selectedTimeInterval, setSelectedTimeInterval] = useState<
    'daily' | 'weekly' | 'monthly' | 'yearly'
  >('daily');

  const [isChartdataEmpty, setIsChardataEmpty] = useState<boolean>(false);

  const [rawBreakdownData, setRawBreakdownData] = useState<any>(null);

  const selectedProductIds = selectedPresentations;

  // Call IHMA hook only when needed
  const { data: ihmaChartData, isLoading: ihmaLoading } = useGetIhmaPriceBreakdown(
    {
      productId: selectedProductIds[0] || 0,
      startMonthEpoch: selectedDateRange
        ? selectedDateRange[0].startOf('month').valueOf()
        : undefined,
      endMonthEpoch: selectedDateRange
        ? selectedDateRange[1].startOf('month').valueOf()
        : undefined,
      timezoneOffset: new Date().getTimezoneOffset(),
    },
    priceSource === PriceSourceTypeEnum.IHMA && !!selectedProductIds[0]
  );

  // BCH data
  const { data: bchChartData, isLoading: bchLoading } = useGetBCHOverlay(
    {
      type: selectedBCHType || '',
      mode: selectedBCHType ? selectedBCHMode : CpiModeEnum.MONTHLY,
      division: selectedBCHDivision,
      region: selectedBCHRegion,
      startTime: selectedDateRange
        ? selectedDateRange[0].startOf('day').valueOf()
        : dayjs().subtract(1, 'year').startOf('day').valueOf(),
      endTime: selectedDateRange
        ? selectedDateRange[1].endOf('day').valueOf()
        : dayjs().endOf('day').valueOf(),
      timezoneOffset: new Date().getTimezoneOffset(),
    },
    priceSource === PriceSourceTypeEnum.BCH && !!selectedBCHType
  );

  const [chartData, setChartData] = useState<{
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
      color?: string;
      type?: string;
    }>;
    timeInterval: string;
    latestIhmaPrice?: number;
    latestReserveAmount?: number;
  }>({
    categories: [],
    series: [],
    timeInterval: 'daily',
    latestIhmaPrice: undefined,
    latestReserveAmount: undefined,
  });

  const [presentationLoading, setPresentationLoading] = useState<boolean>(false);

  const { isOnline } = useIsOnlineContext();

  const filtersRef = useRef({
    basket: {} as { label: string; value: number },
    presentations: [] as { label: string; value: string }[],
  });

  const externalDataUploadTypeOptions = useMemo(() => getExternalDataUploadTypeOptions(t), [t]);

  const BCHModeOptions = useMemo(() => getCPIModeOptions(t), [t]);

  const chartId = 'priceVarianceChart';

  const basketItems = [
    ...baskets.map((basket) => ({
      label: basket.name,
      value: basket.id,
    })),
  ];

  const departmentItems = [
    ...Array.from(new Set(locations.map((location) => location.district))).map((district) => ({
      label: district,
      value: district,
    })),
  ];

  const municipalityItems: any = [
    ...municipalities.map((location) => ({
      label: location.city,
      value: location.city,
    })),
  ];

  const sideFilters = [
    { label: t('daily'), value: 'daily' },
    { label: t('weekly'), value: 'weekly' },
    { label: t('monthly'), value: 'monthly' },
    { label: t('yearly'), value: 'yearly' },
  ];

  const handleDepartmentChange = (department: string) => {
    setSelectedDepartment(department);
    setMunicipalities([]);
    setSelectedMunicipalities([]);

    if (department) {
      try {
        const locationIdsForSelectedDepartment = locations
          ?.filter((location) => location?.district === department)
          ?.map((location) => location?.id);

        setSelectedLocationIds(locationIdsForSelectedDepartment);
        const filteredMunicipalities = locations?.filter(
          (location) => location?.district === department
        );
        setMunicipalities(filteredMunicipalities);
      } catch (error) {
        errorNotification(t('failedToLoadMunicipalities'));
      }
    } else {
      setMunicipalities([]);
      setSelectedMunicipalities([]);
      setSelectedLocationIds([]);
    }
  };

  const handleMunicipalityChange = (municipalityArr: any) => {
    if (municipalityArr?.length > 0 && selectedDepartment) {
      const locationIdsForSelectedMunicipalities = locations
        ?.filter(
          (location) =>
            location?.district === selectedDepartment && municipalityArr?.includes(location?.city)
        )
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipalities);
    } else if (municipalityArr?.length > 0) {
      const locationIdsForSelectedMunicipality = locations
        ?.filter((location) => municipalityArr?.includes(location?.city))
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipality);
    } else if (selectedDepartment) {
      const locationIdsForSelectedDepartment = locations
        ?.filter((location) => location?.district === selectedDepartment)
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedDepartment);
    } else {
      setSelectedLocationIds([]);
    }

    setSelectedMunicipalities(municipalityArr);
  };

  const handleTimeIntervalChange = (filter: string | number) => {
    setSelectedTimeInterval(filter as 'daily' | 'weekly' | 'monthly' | 'yearly');
  };

  const handleBasketChange = (value: any) => {
    setSelectedBasket(value);
    setSelectedPresentations([]);
  };

  const loadPresentationsForSelection = async () => {
    try {
      setPresentationLoading(true);
      const allPresentations = await referenceDataSyncService.getPresentationsForFilters(
        selectedBasket || undefined
      );
      const presentationOptionArr = allPresentations.map((presentation: any) => ({
        label: presentation?.name,
        value: presentation?.id,
        id: presentation?.id,
      }));

      setPresentations(presentationOptionArr);
    } catch (error) {
      console.error('Error loading presentations for selection:', error);
      setPresentations([]);
    } finally {
      setPresentationLoading(false);
    }
  };

  useEffect(() => {
    loadPresentationsForSelection();
  }, [selectedBasket]);

  // 1. Fetch data strictly for the Default Chart
  const fetchChartData = async () => {
    if (!selectedBasket) {
      return;
    }

    const targetInterval =
      priceSource === PriceSourceTypeEnum.BCH
        ? selectedBCHMode === CpiModeEnum.ANNUAL_AVERAGE
          ? 'yearly'
          : 'monthly'
        : priceSource === PriceSourceTypeEnum.IHMA
          ? 'monthly'
          : selectedTimeInterval;

    try {
      setLoading(true);
      const params = {
        basketId: selectedBasket || undefined,
        productIds: selectedProductIds.length > 0 ? selectedProductIds : undefined,
        locationIds: selectedLocationIds,
        startTime: selectedDateRange && selectedDateRange[0].startOf('day').valueOf(),
        endTime: selectedDateRange && selectedDateRange[1].endOf('day').valueOf(),
        timeInterval: targetInterval,
        timezoneOffset: new Date().getTimezoneOffset(),
      };

      const data = await analysisService.getPriceBreakdown(params);

      setRawBreakdownData(data);

      if (priceSource !== PriceSourceTypeEnum.BCH && priceSource !== PriceSourceTypeEnum.IHMA) {
        const dataAvailailityCheck =
          data && data?.series?.every((item) => item?.data?.length === 0);
        setIsChardataEmpty(dataAvailailityCheck);
        setChartData(data);
      }
    } catch (error) {
      errorNotification(t('failedToLoadChartData'));
      if (priceSource !== PriceSourceTypeEnum.BCH && priceSource !== PriceSourceTypeEnum.IHMA) {
        setIsChardataEmpty(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setFilterLoading(true);
      try {
        setPresentationLoading(true);
        const basketsData = await referenceDataSyncService.getBaskets();
        setBaskets(basketsData as unknown as Basket[]);
        if (selectedBasket == null) {
          setSelectedBasket(await referenceDataSyncService.getBasicFoodBasketId());
        }

        const locationsData = await referenceDataSyncService.getLocations();
        setLocations(locationsData as unknown as Location[]);
      } catch (error) {
        errorNotification(t('failedToLoadFilters'));
      } finally {
        setPresentationLoading(false);
        setFilterLoading(false);
      }
    };

    loadData();
  }, []);

  // 2. Add priceSource as dependency so it triggers when returning to Default
  useEffect(() => {
    fetchChartData();
  }, [
    selectedBasket,
    selectedPresentations,
    selectedLocationIds,
    selectedTimeInterval,
    selectedDateRange,
    priceSource,
    selectedBCHMode,
    t,
  ]);

  // When IHMA, combine IHMA data and default price variance data
  useEffect(() => {
    if (priceSource === PriceSourceTypeEnum.IHMA) {
      const combinedSet = new Set<string>([
        ...(rawBreakdownData?.categories || []),
        ...(ihmaChartData?.categories || []),
      ]);
      const combinedCategories = Array.from(combinedSet).sort();

      // Average Price from default dataset
      const rawAverageSeries =
        rawBreakdownData?.series?.find((s: any) => s.name === t('averagePrice')) ||
        rawBreakdownData?.series?.[0];

      const priceMap = new Map(
        rawBreakdownData?.categories?.map((category: string, idx: number) => [
          category,
          rawAverageSeries?.data[idx],
        ]) || []
      );

      const alignedAverageData = combinedCategories.map((category: string) => {
        return priceMap.has(category) ? priceMap.get(category) : null;
      });

      const baseSeries = {
        name: rawAverageSeries?.name || '',
        type: 'area',
        data: alignedAverageData as number[],
        color: rawAverageSeries?.color,
      };

      // IHMA Price dataset alignment
      const rawIhmaSeries = ihmaChartData?.series?.[0];
      const ihmaMap = new Map(
        ihmaChartData?.categories?.map((category: string, idx: number) => [
          category,
          rawIhmaSeries?.data[idx],
        ]) || []
      );

      const alignedIhmaData = combinedCategories.map((category: string) => {
        return ihmaMap.has(category) ? ihmaMap.get(category) : null;
      });

      const ihmaSeries = {
        name: rawIhmaSeries?.name || '',
        type: 'area',
        data: alignedIhmaData as number[],
        color: rawIhmaSeries?.color,
      };

      const series = [baseSeries];
      if (selectedPresentations.length > 0 && rawIhmaSeries) {
        series.push(ihmaSeries);
      }

      const cleanSeries = series.filter((s) =>
        s.data.some((val: any) => val !== null && val !== undefined)
      );

      setChartData({
        categories: combinedCategories,
        series: cleanSeries,
        timeInterval: 'monthly',
        latestIhmaPrice: ihmaChartData?.latestIhmaPrice,
        latestReserveAmount: ihmaChartData?.latestReserveAmount,
      });

      const hasBreakdownData = rawBreakdownData?.series?.some((s: any) => s?.data?.length > 0);
      const hasIhmaData =
        selectedPresentations.length > 0 && rawIhmaSeries?.data && rawIhmaSeries?.data?.length > 0;

      setIsChardataEmpty(!hasBreakdownData && !hasIhmaData);
    }
  }, [ihmaChartData, rawBreakdownData, priceSource, t]);

  const handlePriceSourceChange = (value: PriceSourceType) => {
    setPriceSource(value);

    if (value === PriceSourceTypeEnum.IHMA) {
      setSelectedPresentations((prev) => prev.slice(0, 1));
      resetBCHFilters();
    } else if (value === PriceSourceTypeEnum.BCH) {
      const oneYearAgo = dayjs().subtract(1, 'year').startOf('day');
      const today = dayjs().endOf('day');
      setSelectedDateRange([oneYearAgo, today]);
      setSelectedBCHMode(CpiModeEnum.MONTHLY);
      setSelectedBCHType(ExternalDataUploadTypeEnum.CPI_MONTHLY_SERIES_AND_ANNUAL_AVERAGE);
    } else {
      setSelectedTimeInterval('daily');
      resetBCHFilters();
    }
  };

  // When BCH, combine BCH data and default price variance data
  useEffect(() => {
    if (priceSource === PriceSourceTypeEnum.BCH) {
      const combinedCategories =
        bchChartData?.xAxis && bchChartData?.xAxis?.length > 0
          ? bchChartData?.xAxis
          : rawBreakdownData?.categories || [];

      // Average Price
      const rawAverageSeries =
        rawBreakdownData?.series?.find((s: any) => s.name === t('averagePrice')) ||
        rawBreakdownData?.series?.[0];

      const priceMap = new Map(
        rawBreakdownData?.categories?.map((category: string, idx: number) => [
          category,
          rawAverageSeries?.data[idx],
        ]) || []
      );

      const alignedAverageData = combinedCategories.map((bchCategory: string) => {
        if (priceMap.has(bchCategory)) return priceMap.get(bchCategory);
        const fuzzyKey = Array.from(priceMap.keys()).find((key: any) =>
          key.startsWith(bchCategory)
        );
        return fuzzyKey ? priceMap.get(fuzzyKey) : null;
      });

      const baseSeries = {
        name: t('averagePrice'),
        type: 'area',
        data: alignedAverageData,
      };

      const bchSeries = {
        name: selectedBCHType
          ? t(`dataUpload:externalDataUploadTypes.${selectedBCHType}`)
          : t('BCH'),
        type: 'area',
        data:
          bchChartData?.cpiSeries && bchChartData?.cpiSeries?.length > 0
            ? bchChartData?.cpiSeries
            : new Array(combinedCategories?.length).fill(null),
      };

      const series = [baseSeries, bchSeries];
      const cleanSeries = series.filter((s) =>
        s.data.some((val: any) => val !== null && val !== undefined)
      );

      setChartData({
        categories: combinedCategories,
        series: cleanSeries,
        timeInterval: selectedBCHMode === CpiModeEnum.ANNUAL_AVERAGE ? 'yearly' : 'monthly',
      });

      const hasBreakdownData = rawBreakdownData?.series?.some((s: any) => s?.data?.length > 0);
      const hasBchData = bchChartData?.cpiSeries && bchChartData?.cpiSeries?.length > 0;

      setIsChardataEmpty(!hasBreakdownData && !hasBchData);
    }
  }, [bchChartData, rawBreakdownData, priceSource, selectedBCHType, selectedBCHMode, t]);

  const updateBCHFilters = (value: any) => {
    setSelectedBCHType(value);

    if (
      value === ExternalDataUploadTypeEnum.CPI_DIVISION_MONTHLY_SERIES ||
      value === ExternalDataUploadTypeEnum.CPI_REGION_MONTHLY_SERIES
    ) {
      setSelectedBCHMode(CpiModeEnum.MONTHLY);
    }

    if (
      value === ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_DIVISION ||
      value === ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_REGION
    ) {
      setSelectedBCHMode(CpiModeEnum.ANNUAL_AVERAGE);
    }

    if (isDivisionFilterVisible(value) && BCHDivisionsOptions?.length > 0) {
      setSelectedBCHDivision(BCHDivisionsOptions[0].value);
      setSelectedBCHRegion(undefined);
    } else if (isRegionFilterVisible(value) && BCHRegionsOptions?.length > 0) {
      setSelectedBCHRegion(BCHRegionsOptions[0].value);
      setSelectedBCHDivision(undefined);
    } else {
      setSelectedBCHRegion(undefined);
      setSelectedBCHDivision(undefined);
    }
  };

  const resetBCHFilters = () => {
    setSelectedBCHType(null);
    setSelectedBCHRegion(undefined);
    setSelectedBCHDivision(undefined);
  };

  const downloadPNG = async () => {
    const dataURI = await ApexCharts.exec(chartId, 'dataURI');

    const link = document.createElement('a');
    link.href = dataURI.imgURI;
    link.download = `${t(`chartTitlePriceVariationTrends`)}_${Date.now()}.png`;
    link.click();

    await ApexCharts.exec(
      chartId,
      'updateOptions',
      {
        xaxis: { min: currentZoom.current.min, max: currentZoom.current.max },
        annotations: {
          texts: [],
        },
      },
      false,
      true
    );
  };

  const getPresentationLabelById = (productId?: number) => {
    if (!productId) return undefined;

    const presentationOption = presentations.find(
      (presentation: any) => presentation?.value === productId || presentation?.id === productId
    );

    return presentationOption?.label || String(productId);
  };

  const buildFilterOptionsForCSVExport = () => {
    if (!filtersRef.current.basket?.label) {
      const option = basketItems.find((o) => o.value === selectedBasket);
      filtersRef.current.basket = option as any;
    }

    const selectedPresentationLabels = selectedPresentations.map((productId) =>
      getPresentationLabelById(productId)
    );

    const timeIntervalValue =
      priceSource === PriceSourceTypeEnum.BCH
        ? BCHModeOptions.find((option) => option.value === selectedBCHMode)?.label ||
        t(`${selectedBCHMode || CpiModeEnum.MONTHLY}`)
        : t(`${selectedTimeInterval}`);

    const normalFilterData = [
      {
        key: 'dateRange',
        label: t('downloadFilters.dateRange'),
        value: `[${selectedDateRange?.[0]?.format('YYYY-MM-DD')},${selectedDateRange?.[1]?.format('YYYY-MM-DD')}]`,
      },
      {
        key: 'timeInterval',
        label: t('timeInterval'),
        value: timeIntervalValue,
      },
      {
        key: 'basket',
        label: t('downloadFilters.basket'),
        value: filtersRef.current.basket?.label,
      },
      {
        key: 'presentations',
        label: t('presentations'),
        value: selectedPresentationLabels,
      },
      {
        key: 'department',
        label: t('downloadFilters.department'),
        value: selectedDepartment,
      },
      {
        key: 'municipalities',
        label: t('downloadFilters.municipalities'),
        value: selectedMunicipalities,
      },
    ];

    if (priceSource === PriceSourceTypeEnum.BCH) {
      return [
        ...normalFilterData,
        {
          key: 'priceSource',
          label: t('priceSource'),
          value: t(priceSource),
        },
        {
          key: 'bchDatasetType',
          label: t('dataUpload:externalDataUploadType'),
          value: selectedBCHType
            ? t(`dataUpload:externalDataUploadTypes.${selectedBCHType}`)
            : undefined,
        },
      ];
    }

    if (priceSource === PriceSourceTypeEnum.IHMA) {
      return [
        ...normalFilterData,
        {
          key: 'priceSource',
          label: t('priceSource'),
          value: t(priceSource),
        },
      ];
    }

    return normalFilterData;
  };

  const currentZoom = useRef({ min: null, max: null });

  return (
    <div id="price-variance-pdf-content" className="chart bg-white">
      <section className="mb-2 mt-2 flex w-full flex-col justify-start gap-4 md:mb-16 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h3 className="text-2xl font-semibold">{t('priceVariance')}</h3>
        </div>
        {!isChartdataEmpty && (
          <div data-html2canvas-ignore="true" className="flex justify-end gap-3 text-sm md:gap-4">
            <PrimaryButton
              disabled={isDownloading || !isOnline}
              className="px-3"
              handleOnClick={() => {
                if (isDownloading) return;
                setIsDownloading(true);
                try {
                  downloadPNG();
                } finally {
                  setTimeout(() => setIsDownloading(false), 500);
                }
              }}
            >
              {t('downloadPNG')}
            </PrimaryButton>
            <PrimaryButton
              disabled={isDownloading || !isOnline}
              className="px-3"
              handleOnClick={() => {
                if (isDownloading) return;
                const filterData = buildFilterOptionsForCSVExport();

                setIsDownloading(true);
                try {
                  const seriesNameMap = [t('averagePrice'), t('minPrice'), t('maxPrice')];
                  const translatedChartData = {
                    ...chartData,
                    series: chartData.series.map((s: any, i: number) => ({
                      ...s,
                      name: seriesNameMap[i] || s.name,
                    })),
                  };
                  downloadCustomCSV(
                    t('chartTitlePriceVariationTrends'),
                    translatedChartData,
                    filterData,
                    t,
                    t('category')
                  );
                } finally {
                  setTimeout(() => setIsDownloading(false), 500);
                }
              }}
            >
              {t('downloadCSV')}
            </PrimaryButton>
          </div>
        )}
      </section>

      {/* Price Source Dropdown */}
      <div
        className={`mb-6 grid w-full grid-cols-1 items-start justify-between gap-6 overflow-visible md:grid-cols-4 lg:grid-cols-4 lg:items-center`}
      >
        <FormItem
          className="mb-0"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('priceSource')}</span>}
        >
          <Select
            value={priceSource}
            onChange={handlePriceSourceChange}
            placeholder={t('defaultPriceVariance')}
            allowClear
            options={Object.values(PriceSourceTypeEnum).map((source) => ({
              label: t(source),
              value: source,
            }))}
            className="[&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>

        <FormItem
          className="relative col-span-1 mb-0 w-full 3xl:col-span-1"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('dateFilter')}</span>}
        >
          <DatePicker.RangePicker
            className="mt-2 w-full rounded-lg px-4 py-2 md:mt-0"
            value={selectedDateRange}
            onChange={(dates) => setSelectedDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
            format="YYYY-MM-DD"
            placeholder={[t('from'), t('to')]}
            disabledDate={disableFutureDates}
            allowClear={false}
          />
        </FormItem>
      </div>

      {/* Default Filters (Show ONLY when priceSource is null/undefined) */}
      <div className="mb-6 mt-4 grid w-full grid-cols-1 items-start justify-between gap-6 overflow-visible md:grid-cols-4 lg:items-center">
        <FormItem
          className="col-span-2 mb-0 w-full md:col-span-1"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('basket')}</span>}
        >
          <Select
            value={selectedBasket}
            options={basketItems}
            placeholder={t('basketPlaceholder')}
            showSearch
            onChange={(value, option) => {
              handleBasketChange(value);
              filtersRef.current.basket = option as any;
            }}
            filterOption={(input, option) =>
              normalize(option?.label ?? '')
                .toString()
                .toLowerCase()
                .includes(normalize(input.toLowerCase()))
            }
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>
        <FormItem
          className="col-span-2 mb-0 w-full md:col-span-1"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('presentations')}</span>}
        >
          <Select
            value={
              priceSource === PriceSourceTypeEnum.IHMA
                ? selectedPresentations[0]
                : selectedPresentations
            }
            options={presentations}
            loading={presentationLoading}
            placeholder={t('presentationsPlaceholder')}
            allowClear
            onClear={() => setSelectedPresentations([])}
            showSearch
            mode={priceSource === PriceSourceTypeEnum.IHMA ? undefined : 'multiple'}
            maxTagCount={1}
            maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 20 : 10}
            onChange={(value, option) => {
              const updatedVal = Array.isArray(value) ? value : value ? [value] : [];
              setSelectedPresentations(updatedVal);
              filtersRef.current.presentations = (
                Array.isArray(option) ? option : option ? [option] : []
              ) as any;
            }}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>

        <FormItem
          className="col-span-2 mb-0 w-full md:col-span-1"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('department')}</span>}
        >
          <Select
            value={selectedDepartment}
            options={departmentItems}
            placeholder={t('departmentPlaceholder')}
            allowClear
            showSearch
            onChange={(value) => {
              handleDepartmentChange(value);
            }}
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>
        <FormItem
          className="col-span-2 mb-0 w-full md:col-span-1"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('municipality')}</span>}
        >
          <Select
            value={selectedMunicipalities}
            options={municipalityItems}
            placeholder={t('municipalityPlaceholder')}
            allowClear
            onClear={() => setSelectedMunicipalities([])}
            showSearch
            mode="multiple"
            maxTagCount={1}
            maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 20 : 10}
            disabled={!selectedDepartment}
            onChange={handleMunicipalityChange}
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>
      </div>

      {/*---------- BCH Filters (Show ONLY when BCH is selected) ---------*/}
      {/* BCH Dataset type filter */}
      {priceSource === PriceSourceTypeEnum.BCH && (
        <div className="relative mb-6 mt-8 w-full rounded-xl border border-solid border-gray-300 p-3 pt-6">
          <span className="absolute -top-3 left-4 bg-white px-2 text-xs font-semibold tracking-wider text-gray-600">
            {t('BCHFilter')}
          </span>

          <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-4">
            {/* BCH Dataset type filter */}
            <FormItem
              className="mb-0 w-full"
              layout="vertical"
              label={
                <span className="text-sm font-medium text-gray-700">
                  {t('dataUpload:externalDataUploadType')}
                </span>
              }
              name={'externalDataUploadType'}
            >
              <Select
                value={selectedBCHType}
                onChange={(value) => updateBCHFilters(value)}
                options={externalDataUploadTypeOptions}
                placeholder={t('dataUpload:externalDataUploadTypePlaceholder')}
                className="[&>.ant-select-selector]:!rounded-lg"
                defaultValue={ExternalDataUploadTypeEnum.CPI_MONTHLY_SERIES_AND_ANNUAL_AVERAGE}
              />
            </FormItem>

            {/* BCH Division filter */}
            {selectedBCHType &&
              (selectedBCHType === ExternalDataUploadTypeEnum.CPI_DIVISION_MONTHLY_SERIES ||
                selectedBCHType === ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_DIVISION) && (
                <FormItem
                  className="mb-0 w-full"
                  layout="vertical"
                  label={<span className="text-sm font-medium text-gray-700">{t('division')}</span>}
                >
                  <Select
                    value={selectedBCHDivision}
                    onChange={setSelectedBCHDivision}
                    options={BCHDivisionsOptions}
                    placeholder={t('divisionPlaceholder')}
                    showSearch
                    filterOption={(input, option: any) =>
                      (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                    className="[&>.ant-select-selector]:!rounded-lg"
                  />
                </FormItem>
              )}

            {/* BCH Region filter */}
            {selectedBCHType &&
              (selectedBCHType === ExternalDataUploadTypeEnum.CPI_REGION_MONTHLY_SERIES ||
                selectedBCHType === ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_REGION) && (
                <FormItem
                  className="mb-0 w-full"
                  layout="vertical"
                  label={<span className="text-sm font-medium text-gray-700">{t('region')}</span>}
                >
                  <Select
                    value={selectedBCHRegion}
                    onChange={setSelectedBCHRegion}
                    options={BCHRegionsOptions}
                    placeholder={t('regionPlaceholder')}
                    className="[&>.ant-select-selector]:!rounded-lg"
                    showSearch
                    filterOption={(input, option: any) =>
                      (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </FormItem>
              )}
          </div>
        </div>
      )}

      {priceSource !== PriceSourceTypeEnum.IHMA && (
        <div className="flex flex-col justify-end lg:flex-row">
          <div className="mt-4 lg:mt-0">
            <Select
              popupClassName="!w-[150px]"
              className="w-[max(fit-content,120px)] [&>.ant-select-selector]:!border-none [&>.ant-select-selector]:!pl-0 [&_.ant-select-selection-item]:!text-base [&_.ant-select-selection-item]:!text-black [&_.ant-select-selection-placeholder]:!text-base [&_.ant-select-selection-placeholder]:!text-black [&_.ant-select-selection-placeholder]:hover:!text-black/50 [&_input]:!capitalize"
              options={
                priceSource === PriceSourceTypeEnum.BCH
                  ? BCHModeOptions.map((option) => {
                    const isMonthlyDisabled =
                      option.value === CpiModeEnum.MONTHLY &&
                      (selectedBCHType ===
                        ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_DIVISION ||
                        selectedBCHType ===
                        ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_REGION);

                    const isYearlyDisabled =
                      option.value === CpiModeEnum.ANNUAL_AVERAGE &&
                      (selectedBCHType ===
                        ExternalDataUploadTypeEnum.CPI_DIVISION_MONTHLY_SERIES ||
                        selectedBCHType === ExternalDataUploadTypeEnum.CPI_REGION_MONTHLY_SERIES);

                    return {
                      ...option,
                      disabled: isMonthlyDisabled || isYearlyDisabled,
                    };
                  })
                  : sideFilters.map((filter) => ({
                    label: filter.label,
                    value: filter.value,
                  }))
              }
              defaultValue={
                priceSource === PriceSourceTypeEnum.BCH
                  ? BCHModeOptions[0]?.value
                  : sideFilters[0].value
              }
              placeholder={t('select')}
              onChange={(value: string) =>
                priceSource === PriceSourceTypeEnum.BCH
                  ? setSelectedBCHMode(value)
                  : handleTimeIntervalChange(value)
              }
              value={
                priceSource === PriceSourceTypeEnum.BCH
                  ? selectedBCHType === ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_DIVISION ||
                    selectedBCHType === ExternalDataUploadTypeEnum.ANNUAL_AVERAGE_CPI_BY_REGION
                    ? CpiModeEnum.ANNUAL_AVERAGE
                    : selectedBCHMode || CpiModeEnum.MONTHLY
                  : selectedTimeInterval
              }
            />
          </div>
        </div>
      )}

      {isChartdataEmpty ? (
        <div key="no-data" className="mb-6 py-8 text-center text-sm text-gray-500">
          {t('noDataAvailable')}
        </div>
      ) : (
        <ChartComponent
          title={''}
          type={CHART_TYPES.AREA}
          series={chartData.series}
          options={{
            chart: {
              id: chartId,
              toolbar: {
                show: true,
                tools: {
                  download: false,
                  selection: true,
                  zoom: true,
                  zoomin: true,
                  zoomout: true,
                  pan: true,
                  reset: true,
                },
              },
              events: {
                zoomed(_chart, { xaxis }) {
                  currentZoom.current = xaxis;
                },
              },
            },
            stroke: {
              curve: 'straight',
              width: 2,
              dashArray: 5,
            },
            markers: {
              size: 4,
            },
            dataLabels: {
              enabled: false,
            },
            xaxis: {
              categories: chartData.categories,
            },
            yaxis:
              priceSource === PriceSourceTypeEnum.BCH
                ? [
                  {
                    title: {
                      text: t('averagePrice'),
                      style: {
                        fontFamily: 'Inter, Roboto, sans-serif',
                      },
                    },
                    labels: {
                      formatter: (value) => {
                        if (value === undefined || value === null) return '';
                        return Number(value).toFixed(2);
                      },
                    },
                  },
                  {
                    opposite: true,
                    title: {
                      text: t('CPIYaxis'),
                      style: {
                        fontFamily: 'Inter, Roboto, sans-serif',
                      },
                    },
                    labels: {
                      formatter: (value) => {
                        if (value === undefined || value === null) return '';
                        return Number(value).toFixed(2);
                      },
                    },
                  },
                ]
                : {
                  title: {
                    text: t('priceYaxis'),
                    style: {
                      fontFamily: 'Inter, Roboto, sans-serif',
                    },
                  },
                  labels: {
                    formatter: (value) => {
                      if (value === undefined || value === null) return '';
                      return Number(value).toFixed(2);
                    },
                  },
                },
            // colors: ['#00b050', '#ff0000', '#0070c0'],
            colors: chartData.series.map((s, index) => {
              if (priceSource === PriceSourceTypeEnum.BCH && s.name !== t('averagePrice')) {
                return '#ff0000';
              }

              const defaultColors = ['#00b050', '#ff0000', '#0070c0'];
              return defaultColors[index] || '#00b050';
            }),
            legend: {
              position: 'bottom',
              showForSingleSeries: true,
            },
            grid: {
              borderColor: '#e7e7e7',
              strokeDashArray: 4,
            },
            fill: {
              type: 'gradient',
              gradient: {
                gradientToColors: ['#A8D0F3', '#E0E7F5'],
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.5,
                stops: [0, 90, 100],
              },
            },
          }}
          maxHeight={400}
          loading={
            loading ||
            filterLoading ||
            (priceSource === PriceSourceTypeEnum.IHMA && ihmaLoading) ||
            (priceSource === PriceSourceTypeEnum.BCH && bchLoading)
          }
        />
      )}

      {priceSource === PriceSourceTypeEnum.IHMA && selectedPresentations.length > 0 && (
        <div className="mb-6 mt-6 grid w-full grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-gray-300 p-4">
            <p className="text-m text-gray-600">{t('currentIhmaPrice')}</p>
            <p className="text-xl font-semibold text-blue-600">
              {chartData.latestIhmaPrice ? Number(chartData.latestIhmaPrice).toFixed(2) : '_'}
            </p>
          </div>
          <div className="rounded-lg border border-gray-300 p-4">
            <p className="text-m text-gray-600">{t('currentReserveAmount')}</p>
            <p className="text-xl font-semibold text-green-600">
              {chartData.latestReserveAmount
                ? Number(chartData.latestReserveAmount).toFixed(2)
                : '_'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceVarianceChart;
